<template>
  <!-- Chat header -->
  <header class="mv-chat-header">
    <button class="mv-icon-btn" @click="$emit('back')" title="Voltar">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="15 18 9 12 15 6"/>
      </svg>
    </button>
    <div class="mv-item-av-wrap" style="margin: 0 6px 0 2px; position: relative; flex-shrink: 0;">
      <div class="mv-chat-av" :style="{ background: avatarColor(conv.contact.name) }">
        <img
          v-if="conv.contact.thumbnail"
          :src="conv.contact.thumbnail"
          :alt="conv.contact.name"
          @error="(e) => (e.target as HTMLImageElement).style.display='none'"
        />
        <span v-else>{{ initials(conv.contact.name) }}</span>
      </div>
      <span class="mv-status-dot sm" :class="dotClass(conv)"></span>
    </div>
    <div class="mv-chat-hinfo">
      <div class="mv-chat-hname">{{ conv.contact.name }}</div>
      <div v-if="convDocument" class="mv-chat-hdoc">{{ convDocument.label }}: {{ convDocument.value }}</div>
      <div v-if="conv.contact.phone" class="mv-chat-hphone">
        <span>{{ fmtPhone(conv.contact.phone) }}</span>
        <button
          type="button"
          class="mv-phone-btn"
          :class="{ 'is-copied': phoneCopied }"
          @click.stop="copyPhone"
          :title="phoneCopied ? 'Copiado!' : 'Copiar número'"
        >
          <svg v-if="phoneCopied" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <svg v-else width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
          </svg>
        </button>
        <a class="mv-phone-btn mv-phone-call" :href="`tel:${conv.contact.phone}`" @click.stop title="Ligar">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.44 2 2 0 0 1 3.59 2.24h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
        </a>
      </div>
      <div class="mv-chat-hsub">
        <span v-if="conv.team">{{ conv.team.name }} · </span>#{{ conv.id }}
      </div>
    </div>
    <button class="mv-icon-btn" @click="$emit('open-msg-search')" title="Buscar nas mensagens">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
    </button>
    <button class="mv-icon-btn" :class="{ 'mv-icon-btn--active': macroSheetOpen }" @click="$emit('open-macro-sheet')" title="Macros">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <polygon points="5 3 19 12 5 21 5 3"/>
      </svg>
    </button>
  </header>

  <!-- Action bar: status + agent + labels -->
  <div class="mv-chat-action-bar">
    <div @click.stop>
      <button class="mv-chat-status-btn" @click="$emit('toggle-status-menu')">
        <span class="mv-sdot" :class="conv.status"></span>
        <span class="mv-sdot-label">{{ convStatusLabel(conv.status) }}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
    </div>
    <!-- Agente -->
    <div @click.stop>
      <button class="mv-chat-agent-btn" @click="$emit('open-agent-sheet')">
        <div
          class="mv-chat-agent-av"
          :style="{ background: assignee ? avatarColor(assignee.name) : '#D1D5DB' }"
        >
          <img
            v-if="assignee?.thumbnail"
            :src="assignee.thumbnail"
            :alt="assignee.name"
            @error="(e) => (e.target as HTMLImageElement).style.display='none'"
          />
          <span v-else-if="assignee">{{ initials(assignee.name) }}</span>
          <svg v-else width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
        <span class="mv-chat-agent-name">{{ assignee?.name ?? 'agente' }}</span>
      </button>
    </div>

    <div class="mv-chat-lbls-wrap">
      <span v-for="l in currentLabels" :key="l" class="mv-lbl-chip">
        {{ l.replace(/_/g, ' ') }}
        <button class="mv-lbl-chip-x" @click.stop="$emit('toggle-label', l)" :disabled="updatingLabels">×</button>
      </span>
      <button class="mv-lbl-add" @click.stop="$emit('toggle-labels')" :disabled="updatingLabels">
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        etiqueta
      </button>
    </div>
  </div>

  <!-- SLA banner -->
  <div v-if="activeSlaBadge" :class="['mv-sla-banner', activeSlaBadge.status]">
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
    <strong>{{ activeSlaBadge.time }}</strong>
    <span>· SLA {{ activeSlaBadge.label }}{{ activeSlaBadge.status === 'breached' ? ' em atraso' : ' em risco' }}</span>
  </div>

  <!-- Snooze banner -->
  <div v-if="conv.status === 'snoozed'" class="mv-snooze-banner">
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
    <span v-if="snoozeRemainingLabel">Adiado — {{ snoozeRemainingLabel }} restantes</span>
    <span v-else>Adiado até próxima resposta do cliente</span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { Conversation, Assignee } from '@/types/dashboard'
import type { SLABadge } from '@/types/chat'
import { avatarColor, initials, conversationDocument } from '@/composables/useHelpers'
import { dotClass, convStatusLabel } from '@/utils/mobileFormat'
import { fmtPhone } from '@/utils/contactsFormat'

const props = defineProps<{
  conv: Conversation
  macroSheetOpen: boolean
  assignee: Assignee | null
  currentLabels: string[]
  updatingLabels: boolean
  activeSlaBadge: SLABadge | null
  snoozeRemainingLabel: string | null
}>()

const convDocument = computed(() => conversationDocument(props.conv))

// Mesmo padrão de copiar (ícone → check verde por 2s) já usado em
// ConvDrawer.vue/AvatarPopup.vue no desktop.
const phoneCopied = ref(false)
async function copyPhone() {
  const phone = props.conv.contact.phone
  if (!phone) return
  await navigator.clipboard.writeText(phone)
  phoneCopied.value = true
  setTimeout(() => { phoneCopied.value = false }, 2000)
}

defineEmits<{
  back: []
  'open-msg-search': []
  'open-macro-sheet': []
  'toggle-status-menu': []
  'open-agent-sheet': []
  'toggle-labels': []
  'toggle-label': [label: string]
}>()
</script>

<style scoped>
/* Compartilhado com MobileConvList */
.mv-icon-btn {
  background: rgba(255,255,255,.08);
  border: none;
  color: rgba(255,255,255,.7);
  width: 36px; height: 36px;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background .15s, color .15s;
  text-decoration: none;
}
.mv-icon-btn:hover, .mv-icon-btn:active { background: rgba(255,255,255,.16); color: #fff; }
.mv-icon-btn--active { background: rgba(188,12,36,.25) !important; color: #fff !important; }

.mv-item-av-wrap {
  position: relative;
  flex-shrink: 0;
}

.mv-status-dot {
  position: absolute;
  bottom: 1px; right: 1px;
  width: 13px; height: 13px;
  border-radius: 50%;
  border: 2.5px solid #fff;
}
.mv-status-dot.sm { width: 11px; height: 11px; }
.mv-status-dot.dot-new     { background: #22c55e; animation: pulse-dot 1.8s ease-in-out infinite; }
.mv-status-dot.dot-pending { background: #f97316; }
.mv-status-dot.dot-open    { background: #3b82f6; }
.mv-status-dot.dot-snoozed { background: #d97706; }

@keyframes pulse-dot {
  0%,100% { box-shadow: 0 0 0 0 rgba(34,197,94,.55); }
  50%      { box-shadow: 0 0 0 4px rgba(34,197,94,0); }
}

/* ══ Chat header ══ */
.mv-chat-header {
  background: #021627;
  border-bottom: 2px solid #BC0C24;
  padding: 10px 12px;
  padding-top: max(10px, env(safe-area-inset-top));
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.mv-chat-av {
  width: 36px; height: 36px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 800;
  color: #fff; overflow: hidden;
}
.mv-chat-av img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }

.mv-chat-hinfo {
  flex: 1;
  min-width: 0;
}
.mv-chat-hname {
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mv-chat-hdoc {
  font-size: 10.5px;
  color: rgba(255,255,255,.4);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mv-chat-hsub {
  font-size: 11px;
  color: rgba(255,255,255,.45);
}

.mv-chat-hphone {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 600;
  color: rgba(255,255,255,.7);
  margin-top: 1px;
}
.mv-phone-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 17px;
  height: 17px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255,255,255,.12);
  color: rgba(255,255,255,.8);
  cursor: pointer;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  transition: background .14s, color .14s;
}
.mv-phone-btn:active { background: rgba(255,255,255,.26); color: #fff; }
.mv-phone-btn.is-copied { background: #22c55e; color: #fff; }
.mv-phone-call { background: rgba(34,197,94,.18); color: #4ade80; }
.mv-phone-call:active { background: #22c55e; color: #fff; }

/* ══ Chat action bar ══ */
.mv-chat-action-bar {
  background: #fff;
  border-bottom: 1px solid #E5E7EB;
  padding: 7px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
}
.mv-chat-status-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  background: #F3F4F6;
  border: none;
  border-radius: 20px;
  padding: 5px 10px 5px 8px;
  font-size: 11.5px;
  font-weight: 600;
  color: #374151;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
}
.mv-chat-status-btn:active { background: #E5E7EB; }
.mv-sdot {
  width: 8px; height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
}
.mv-sdot.open     { background: #3b82f6; }
.mv-sdot.resolved { background: #22c55e; }
.mv-sdot.pending  { background: #f97316; }
.mv-sdot.snoozed  { background: #d97706; }
.mv-sdot-label { max-width: 62px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mv-chat-lbls-wrap {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
}
.mv-lbl-chip {
  display: flex;
  align-items: center;
  gap: 3px;
  background: rgba(3,105,161,.1);
  color: #0369A1;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 20px;
  white-space: nowrap;
}
.mv-lbl-chip-x {
  background: none; border: none;
  cursor: pointer; color: inherit;
  font-size: 14px; line-height: 1; padding: 0; opacity: .6;
}
.mv-lbl-chip-x:active { opacity: 1; }
.mv-lbl-add {
  display: flex; align-items: center; gap: 4px;
  background: none;
  border: 1px dashed #D1D5DB;
  border-radius: 20px;
  padding: 3px 9px;
  font-size: 11px; font-weight: 600;
  color: #9CA3AF;
  font-family: 'Poppins', sans-serif;
  cursor: pointer; flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
}
.mv-lbl-add:active { border-color: #9CA3AF; color: #6B7280; }
.mv-lbl-add:disabled { opacity: .5; }

/* ══ Agent button (action bar) ══ */
.mv-chat-agent-btn {
  display: flex; align-items: center; gap: 5px;
  background: none;
  border: 1px dashed #D1D5DB;
  border-radius: 20px;
  padding: 3px 9px 3px 4px;
  font-size: 11px; font-weight: 600;
  color: #6B7280;
  font-family: 'Poppins', sans-serif;
  cursor: pointer; flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  white-space: nowrap;
  max-width: 120px;
}
.mv-chat-agent-btn:active { border-color: #9CA3AF; color: #374151; }
.mv-chat-agent-av {
  width: 18px; height: 18px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; overflow: hidden;
  font-size: 7px; font-weight: 700; color: #fff;
}
.mv-chat-agent-av img { width: 100%; height: 100%; object-fit: cover; }
.mv-chat-agent-name {
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  max-width: 80px;
}

/* ══ SLA banner ══ */
.mv-sla-banner {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 14px;
  font-size: 11.5px; font-weight: 600;
  flex-shrink: 0;
  border-bottom: 1px solid;
}
.mv-sla-banner strong { font-weight: 800; font-size: 12px; }
.mv-sla-banner.breached { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
.mv-sla-banner.atRisk   { background: #fff7ed; color: #c2410c; border-color: #fed7aa; }

/* ══ Snooze banner ══ */
.mv-snooze-banner {
  display: flex; align-items: center; gap: 7px;
  padding: 7px 14px;
  background: #fffbeb;
  border-bottom: 1px solid #fde68a;
  font-size: 11.5px; font-weight: 600; color: #92400e;
  flex-shrink: 0;
}
</style>
